import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft, FileText, FileType, Presentation, Image as ImageIcon, Trash2, Plus,
  Save, Send, Loader2, Video,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import RichTextEditor from "../components/RichTextEditor";
import { uploadTrainingAsset } from "../lib/uploadAsset";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

const KIND_ICON = { pdf: FileText, word: FileType, ppt: Presentation, image: ImageIcon, file: FileText };
const ACCEPT = {
  pdf: ".pdf,application/pdf",
  word: ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ppt: ".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation",
  image: "image/*",
};

const EMPTY = { title: "", description: "", content_html: "", video_url: "", videos: [], attachments: [], knowledge_check: [], passing_score: 80, published: false };

export default function TrainingEditor({ moduleId, onBack }) {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [id, setId] = useState(moduleId === "new" ? null : moduleId);
  const [loading, setLoading] = useState(moduleId !== "new");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const fileRef = useRef(null);
  const kindRef = useRef("pdf");

  useEffect(() => {
    if (moduleId && moduleId !== "new") {
      api.get(`/admin/training/${moduleId}`).then((r) => setForm({ ...EMPTY, ...r.data }))
        .catch((e) => toast({ title: "Couldn't load module", description: e.response?.data?.detail || "", variant: "destructive" }))
        .finally(() => setLoading(false));
    }
  }, [moduleId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const persist = async (published) => {
    if (!form.title.trim()) { toast({ title: "Title required", variant: "destructive" }); return; }
    for (const q of form.knowledge_check) {
      if (!q.question.trim() || (q.options || []).some((o) => !o.trim())) {
        toast({ title: "Incomplete knowledge check", description: "Fill every question and all its options.", variant: "destructive" });
        return;
      }
    }
    const payload = { ...form, published, passing_score: Number(form.passing_score) || 80 };
    setSaving(true);
    try {
      let res;
      if (id) res = await api.put(`/admin/training/${id}`, payload);
      else { res = await api.post("/admin/training", payload); setId(res.data.id); }
      setForm({ ...EMPTY, ...res.data });
      toast({ title: published ? "Module published" : "Draft saved" });
    } catch (e) {
      toast({ title: "Save failed", description: e.response?.data?.detail || "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const pickFile = (kind) => { kindRef.current = kind; fileRef.current.accept = ACCEPT[kind]; fileRef.current.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(kindRef.current);
    try {
      const asset = await uploadTrainingAsset(file, kindRef.current);
      set("attachments", [...(form.attachments || []), asset]);
      toast({ title: "File uploaded", description: asset.name });
    } catch (err) {
      toast({ title: "Upload failed", description: err.message || "", variant: "destructive" });
    } finally { setUploading(""); }
  };
  const removeAttachment = (i) => set("attachments", form.attachments.filter((_, idx) => idx !== i));

  const addQuestion = () => set("knowledge_check", [...form.knowledge_check, { question: "", options: ["", "", "", ""], correct_index: 0 }]);
  const updateQuestion = (i, patch) => set("knowledge_check", form.knowledge_check.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const removeQuestion = (i) => set("knowledge_check", form.knowledge_check.filter((_, idx) => idx !== i));

  if (loading) return <div className="py-10 text-center text-sm text-slate-400">Loading module…</div>;

  return (
    <div className="space-y-5" data-testid="training-editor">
      <div className="flex items-center justify-between">
        <button onClick={onBack} data-testid="editor-back-btn" className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-violet-600">
          <ArrowLeft className="h-4 w-4" /> Back to modules
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => persist(false)} disabled={saving} data-testid="save-draft-btn" className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Draft
          </Button>
          <Button onClick={() => persist(true)} disabled={saving} data-testid="publish-btn" className="gap-1.5 bg-violet-600 text-white hover:bg-violet-700">
            <Send className="h-4 w-4" /> {form.published ? "Update (Published)" : "Publish"}
          </Button>
        </div>
      </div>

      <Card>
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-1.5">
            <Label>Module title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="module-title-input" placeholder="e.g. Customer Service Fundamentals" />
          </div>
          <div className="space-y-1.5">
            <Label>Short description</Label>
            <Input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="module-desc-input" placeholder="One-line summary shown in the module list" />
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="mb-3 text-sm font-bold text-slate-900">Lesson content</h3>
        <RichTextEditor value={form.content_html} onChange={(html) => set("content_html", html)} />
      </Card>

      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Video className="h-4 w-4 text-violet-500" />
          <h3 className="text-sm font-bold text-slate-900">Videos (YouTube or Vimeo)</h3>
        </div>
        <div className="space-y-2">
          {(form.videos || []).map((v, vi) => (
            <div key={vi} className="flex items-center gap-2" data-testid={`video-row-${vi}`}>
              <Input value={v} onChange={(e) => { const arr = [...form.videos]; arr[vi] = e.target.value; set("videos", arr); }} data-testid={`video-input-${vi}`} placeholder="https://www.youtube.com/watch?v=…  or  https://vimeo.com/…" />
              <button onClick={() => set("videos", form.videos.filter((_, idx) => idx !== vi))} className="rounded p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`video-remove-${vi}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {(!form.videos || form.videos.length === 0) && <p className="text-xs text-slate-400">No videos yet.</p>}
        </div>
        <Button variant="outline" size="sm" onClick={() => set("videos", [...(form.videos || []), ""])} data-testid="add-video-btn" className="mt-3 gap-1.5"><Plus className="h-4 w-4" /> Add video</Button>
      </Card>

      <Card>
        <h3 className="mb-3 text-sm font-bold text-slate-900">Attachments</h3>
        <div className="flex flex-wrap gap-2">
          {[["pdf", "Upload PDF"], ["word", "Upload Word"], ["ppt", "Upload PowerPoint"], ["image", "Upload Image"]].map(([kind, label]) => (
            <Button key={kind} variant="outline" size="sm" disabled={!!uploading} onClick={() => pickFile(kind)} data-testid={`upload-${kind}-btn`} className="gap-1.5">
              {uploading === kind ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {label}
            </Button>
          ))}
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="attachment-input" />
        </div>
        <div className="mt-3 space-y-2">
          {(form.attachments || []).map((a, i) => {
            const Icon = KIND_ICON[a.kind] || FileText;
            return (
              <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2" data-testid={`attachment-row-${i}`}>
                <span className="flex items-center gap-2 text-sm text-slate-700"><Icon className="h-4 w-4 text-violet-500" /> {a.name}<span className="text-xs uppercase text-slate-400">{a.kind}</span></span>
                <button onClick={() => removeAttachment(i)} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            );
          })}
          {(!form.attachments || form.attachments.length === 0) && <p className="text-xs text-slate-400">No attachments yet.</p>}
        </div>
      </Card>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">Knowledge check</h3>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Passing score %</Label>
            <Input type="number" min="0" max="100" value={form.passing_score} onChange={(e) => set("passing_score", e.target.value)} data-testid="passing-score-input" className="w-20" />
          </div>
        </div>
        {form.knowledge_check.length === 0 && <p className="mb-3 text-xs text-slate-400">No questions yet. Modules without a knowledge check are completed with a single click. Add questions to require a passing score.</p>}
        <div className="space-y-4">
          {form.knowledge_check.map((q, i) => (
            <div key={i} className="rounded-xl border border-slate-100 p-3" data-testid={`kc-question-${i}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase text-slate-400">Question {i + 1}</span>
                <button onClick={() => removeQuestion(i)} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Textarea rows={2} value={q.question} onChange={(e) => updateQuestion(i, { question: e.target.value })} data-testid={`kc-question-input-${i}`} placeholder="Question text" />
              <div className="mt-2 space-y-2">
                {q.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input type="radio" checked={q.correct_index === oi} onChange={() => updateQuestion(i, { correct_index: oi })} className="accent-violet-600" data-testid={`kc-correct-${i}-${oi}`} />
                    <Input value={opt} onChange={(e) => { const o = [...q.options]; o[oi] = e.target.value; updateQuestion(i, { options: o }); }} data-testid={`kc-option-${i}-${oi}`} placeholder={`Option ${oi + 1}`} />
                  </div>
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-400">Select the radio next to the correct answer.</p>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={addQuestion} data-testid="add-question-btn" className="mt-3 gap-1.5"><Plus className="h-4 w-4" /> Add question</Button>
      </Card>
    </div>
  );
}
